import LWElement from './../../lib/lw-element.js';
import ast from './ast.js';

// Table foot, the factory grid's (sites/factory/js/grid.js): which rows are
// on screen of how many, the rows-per-page choice, and « ‹ page / N › » where
// the page number IS the jump box — type a page, press Enter. Extracted from
// the identical copies every dashboard table carried. It owns no state: the
// `key` attribute names the host's table and every expression delegates to
// the host component's shared table machinery (dashboard-tables.js). Usage:
//
//   <web-pager lw-if="showPager('tickets')" key="tickets" class="table-foot"></web-pager>
//
// Re-rendering: the foot's own clicks update it (sync-handler auto-update),
// and every host update reaches it through leanweb's parent->child
// propagation (4.1+). Mutating clicks also update the host directly:
// client-mode tables re-page synchronously with no async loader to trigger
// the host's auto-update. The two form controls are written by hand after
// every render (syncControls): lw-bind writes attributes, and an input's or
// select's `value` attribute is only its default, not what it shows.
customElements.define('web-pager',
  class extends LWElement {
    constructor() {
      super(ast);
    }

    // Nearest enclosing leanweb component (the dashboard today, a page
    // component tomorrow) — resolved per call, so moving the pager between
    // hosts needs nothing.
    host() {
      let el = this.parentElement;
      while (el && !el.localName.startsWith(leanweb.componentPrefix)) el = el.parentElement;
      return el;
    }

    key() {
      return this.getAttribute('key');
    }

    state() {
      return this.host()?.tableState(this.key()) ?? { page: 1, size: 50 };
    }

    update(rootNode) {
      super.update(rootNode);
      this.syncControls();
    }

    // The select shows the table's size and the box its current page — the
    // pager buttons, a size change and a search reset all land somewhere,
    // and the box must follow. It is left alone only while a page is BEING
    // TYPED into it (input → Enter or blur), not merely while it has focus:
    // a box that keeps the keyboard after Enter (a button pressed with the
    // mouse takes focus in Chrome, not in Safari) must still follow the
    // pages the buttons turn.
    syncControls() {
      const st = this.state();
      const select = this.querySelector('select.page-size');
      if (select) select.value = String(st.size);
      const jump = this.querySelector('input.jump');
      if (jump && !this._typing) jump.value = st.page;
    }

    typing() {
      this._typing = true;
    }

    // Leaving the box without Enter drops what was typed; the box reads the
    // page again.
    settled() {
      this._typing = false;
      this.syncControls();
    }

    info() {
      return this.host()?.pageInfo(this.key()) ?? '';
    }

    total() {
      return this.host()?.tableTotal(this.key()) ?? 0;
    }

    last() {
      return this.host()?.pageCount(this.key()) ?? 1;
    }

    onFirst() {
      return this.host()?.onFirstPage(this.key()) ?? true;
    }

    onLast() {
      return this.host()?.onLastPage(this.key()) ?? true;
    }

    first() {
      const h = this.host();
      if (!h) return;
      h.firstPage(this.key());
      h.update();
    }

    prev() {
      const h = this.host();
      if (!h) return;
      h.prevPage(this.key());
      h.update();
    }

    next() {
      const h = this.host();
      if (!h) return;
      h.nextPage(this.key());
      h.update();
    }

    toLast() {
      const h = this.host();
      if (!h) return;
      h.lastPage(this.key());
      h.update();
    }

    // Enter in the page box goes there; the box then reads back the page the
    // table landed on, so 999 reads back as the last page.
    jump(event) {
      if (event.key !== 'Enter') return;
      const h = this.host();
      if (!h) return;
      this._typing = false;
      h.jumpToPage(this.key(), event.target.value);
      h.update();
      event.target.value = this.state().page;
    }

    setSize(size) {
      const h = this.host();
      if (!h) return;
      h.setPageSize(this.key(), size);
      h.update();
    }
  }
);
